import axios from "axios";
import { generateToken } from "./token";
import { nanoid } from "nanoid";
const apiUrl = process.env.REACT_APP_API_URL;
const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;

const axiosInstance = axios.create({
  baseURL: apiUrl,
  timeout: 100000,
  headers: {
    "Content-Type": "application/json",
  },
});

function generateNonce(length = 16) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  );
}

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem("c10sjuex");
    const tokens = await generateToken();
    const latestToken = nanoid();
    config.headers["xgf-token"] = `${latestToken}`;
    if (token) {
      config.headers.Authorization = `Bearer ${tokens}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function createHmacSHA256(message, key) {
  // Helper function to convert string to byte array
  function strToBytes(str) {
    const encoder = new TextEncoder();
    return encoder.encode(str);
  }

  // XOR the two arrays
  function xorArrays(arr1, arr2) {
    const length = Math.max(arr1.length, arr2.length);
    const result = new Uint8Array(length);

    for (let i = 0; i < length; i++) {
      result[i] = arr1[i % arr1.length] ^ arr2[i % arr2.length];
    }

    return result;
  }

  // SHA-256 implementation
  function sha256(message) {
    const crypto = window.crypto || window.msCrypto;
    const hashBuffer = crypto.subtle.digest("SHA-256", message);
    return hashBuffer.then((hash) => new Uint8Array(hash));
  }

  // Step 1: Key padding
  const blockSize = 64; // SHA-256 block size in bytes (512 bits)
  let keyBytes = strToBytes(key);

  if (keyBytes.length > blockSize) {
    // If key is longer than block size, hash it
    keyBytes = sha256(keyBytes);
  }

  // If key is shorter, pad it with zeros
  if (keyBytes.length < blockSize) {
    const paddedKey = new Uint8Array(blockSize);
    paddedKey.set(keyBytes);
    keyBytes = paddedKey;
  }

  // Step 2: Prepare inner and outer padding
  const ipad = new Uint8Array(blockSize);
  const opad = new Uint8Array(blockSize);

  for (let i = 0; i < blockSize; i++) {
    ipad[i] = keyBytes[i] ^ 0x36; // 0x36 = 0011 0110 in binary
    opad[i] = keyBytes[i] ^ 0x5c; // 0x5c = 0101 1100 in binary
  }

  // Step 3: Compute HMAC
  const innerMessage = new Uint8Array([...ipad, ...strToBytes(message)]);
  const innerHash = sha256(innerMessage);

  return innerHash
    .then((innerHash) => {
      // Step 4: Compute outer HMAC
      const outerMessage = new Uint8Array([...opad, ...innerHash]);
      return sha256(outerMessage);
    })
    .then((finalHash) => {
      // Convert the final hash to a hexadecimal string
      return Array.from(finalHash)
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
    });
}

export default axiosInstance;
